import styles from './index.scss';
import React from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { addToast, ToastType } from '@/store/system';
import classnames from 'classnames/bind';
import { useDispatch } from 'react-redux';
import { useRecoveryPasswordRequestMutation } from '@/services/auth.api';
import LoginBasicBox from '@/components/auth/login-basic-box/login-basic-box';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup/dist/yup';
import * as yup from 'yup';
import SentSuccess from '../SentSuccess';
import { Button, Form, Row } from 'react-bootstrap';
import { useLevvelTranslation } from '@/hooks/useLevvelTranslation';

const cx = classnames.bind(styles);

type LoginForm = {
  account: string;
};

const defaultFormValue: LoginForm = {
  account: '',
};

const ForgotPassword = () => {
  const [isSend, setSend] = React.useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const username = searchParams.get('username');
  const { lt } = useLevvelTranslation();
  const dispatch = useDispatch();
  const schema = yup
    .object({
      account: yup
        .string()
        .trim()
        .max(25, lt('cdb_admin_form_verification_length', { length: 25 }))
        .required(
          `${lt('cdb_admin_username_text')} ${lt('cdb_admin_required_text')}`
        ),
    })
    .required();
  const {
    register,
    reset,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginForm>({
    resolver: yupResolver(schema),
    defaultValues: defaultFormValue,
  });

  const [trigger, { isLoading }] = useRecoveryPasswordRequestMutation();

  const onSubmit = () => {
    handleSendCode();
  };

  // ADR-1 anti-enumeration: the request endpoint answers 200 with an empty body
  // for every outcome — account found, account absent, no email on file, patient
  // account, send limit exhausted. There is deliberately nothing to branch on, so
  // this always lands on the success view. Approved visible changes #1 and #2: the
  // old "account doesn't exist" field error and the old "too frequent" red toast
  // are gone, and their disappearance is the point — a distinguishable failure
  // here is exactly the oracle the ADR closes.
  //
  // NB the endpoint is a mutation now (see auth.api.ts): its trigger resolves to
  // `{data} | {error}` with NO `isSuccess`. Driving the view off `unwrap()`
  // instead is what keeps SentSuccess reachable.
  const handleSendCode = async () => {
    const data = getValues();
    try {
      await trigger({ username: data.account?.trim() }).unwrap();
    } catch (e) {
      // A transport/5xx failure is the one thing that is genuinely not a "sent"
      // signal, but surfacing it per-account would reopen the enumeration
      // channel. Stay on the success view; the BE logs and alarms on delivery.
      console.error(e);
    }
    if (isSend) {
      // Second and later sends keep the frozen resend toast. `ReSetApplied` is
      // the same i18n key the legacy BFF returned as its response `message`
      // (resetpwdapply.go:136), so the wording the user sees is unchanged now
      // that the body is empty and there is no message to echo.
      dispatch(
        addToast({
          msg: lt('ReSetApplied'),
          title: (
            <div className="letter-space3">
              {lt('cdb_message_status_done_text')}
            </div>
          ) as any,
          type: ToastType.Success,
        })
      );
    }
    setSend(true);
  };

  React.useEffect(() => {
    if (username) {
      reset({ account: username });
    }
  }, [username]);

  return (
    <LoginBasicBox>
      <header className={styles.header} style={{ color: 'gray' }}>
        <div
          className={styles.back}
          onClick={() => {
            navigate('/login', { state: { account: username?.trim() } });
          }}>
          <span
            className={cx(['iconfont icon-ico-expand', styles.arrowIcon])}
          />
          &nbsp;
          <span>{lt('common_back')}</span>
        </div>
      </header>
      {isSend ? (
        <SentSuccess onResend={handleSendCode} />
      ) : (
        <main className={styles.main}>
          <section className={cx(['title-bold', 'title'])}>
            {lt('cdb_forgot_password_title')}
          </section>
          <p className={styles.desc} style={{ color: 'gray' }}>
            {lt('cdb_forgot_password_desc')}
          </p>
          <Form noValidate>
            <Row>
              <Form.Group controlId="account" className={cx(['account'])}>
                <Form.Control
                  {...register('account')}
                  className={styles.usernameInput}
                  type="text"
                  autoComplete="off"
                  placeholder={lt('cdb_admin_username_text')}
                />
                {errors.account && (
                  <Form.Control.Feedback
                    type="invalid"
                    className={cx(['invalid-feedback'])}>
                    <div className="mt-1">
                      <span
                        className={cx([
                          'iconfont icon-ico-warning',
                          'warnIcon',
                        ])}
                      />
                      <span className="ml-2">{errors.account?.message}</span>
                    </div>
                  </Form.Control.Feedback>
                )}
              </Form.Group>
            </Row>
          </Form>
          <div className={styles.action}>
            <Button
              className="title-semiBold"
              onClick={handleSubmit(onSubmit)}
              disabled={isLoading}>
              {lt('cdb_forgot_password_send')}
            </Button>
          </div>
        </main>
      )}
    </LoginBasicBox>
  );
};

export default ForgotPassword;
